import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { fariaLimaArticles } from '@/mocks/fariaLimaBlogData';
import { contactInfo, faqItems } from '@/mocks/homeData';
import { useFadeIn } from '@/hooks/useFadeIn';
import Breadcrumb from '@/components/base/Breadcrumb';

function FariaLimaHero() {
  return (
    <section className="relative w-full h-[420px] md:h-[560px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20panoramic%20view%20of%20Faria%20Lima%20business%20district%20in%20Sao%20Paulo%20Brazil%2C%20modern%20glass%20skyscrapers%20reflecting%20golden%20afternoon%20sunlight%2C%20bustling%20financial%20center%20with%20tree-lined%20avenue%2C%20sophisticated%20corporate%20urban%20landscape%2C%20warm%20amber%20and%20cream%20tones%2C%20editorial%20cityscape%20photography%2C%20soft%20atmospheric%20haze%2C%20premium%20professional%20aesthetic%2C%20iconic%20Sao%20Paulo%20skyline&width=1800&height=1120&seq=fl-hero-bg&orientation=landscape"
          alt="Avenida Brigadeiro Faria Lima - São Paulo - centro financeiro"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-300 bg-accent-900/30 backdrop-blur-sm px-3 py-1.5 rounded-full mb-5">
            Blog Exclusivo
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
            Odontologia de Excelência na{' '}
            <span className="italic font-normal text-accent-200">Faria Lima</span>
          </h1>
          <p className="text-white/80 text-sm md:text-base leading-relaxed max-w-xl">
            Conteúdos premium sobre saúde bucal, estética dental e procedimentos odontológicos pensados para quem vive e trabalha no coração financeiro de São Paulo — a Avenida Brigadeiro Faria Lima e região.
          </p>
        </div>
      </div>
    </section>
  );
}

function ArticleCard({ article, index }: { article: typeof fariaLimaArticles[0]; index: number }) {
  const { ref, isVisible } = useFadeIn(0.1);

  return (
    <article
      ref={ref}
      className={`bg-background-50 rounded-xl overflow-hidden card-hover fade-section ${isVisible ? 'is-visible' : ''}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <Link to={`/blog-faria-lima/${article.slug}`} className="block group cursor-pointer">
        <div className="relative overflow-hidden h-48 md:h-52 img-zoom">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-center"
            title={`${article.title} | Dentista Faria Lima`}
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-xs font-medium text-accent-600 bg-accent-50 px-2.5 py-1 rounded-full">
              {article.category}
            </span>
            <span className="text-xs text-foreground-400">{article.readTime}</span>
          </div>
          <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 leading-snug group-hover:text-primary-600 transition-colors duration-300">
            {article.title}
          </h3>
          <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-foreground-400">{article.date}</span>
            <span className="text-xs font-semibold text-primary-500 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              Ler artigo
              <i className="ri-arrow-right-line"></i>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}

function FariaLimaFAQ() {
  const filteredFaq = faqItems.slice(0, 5);

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
            Dúvidas Frequentes
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
            Pacientes da <span className="italic font-normal text-foreground-500">Faria Lima</span> Perguntam
          </h2>
        </div>

        <div className="space-y-3">
          {filteredFaq.map((item, i) => (
            <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
              <summary className="flex items-center justify-between p-5 cursor-pointer">
                <span className="font-heading text-sm md:text-base font-semibold text-foreground-900 pr-4">
                  {item.question}
                </span>
                <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-background-200 rounded-full group-open:bg-primary-100 transition-colors">
                  <i className="ri-add-line text-foreground-600 text-sm group-open:hidden"></i>
                  <i className="ri-subtract-line text-primary-600 text-sm hidden group-open:block"></i>
                </span>
              </summary>
              <div className="px-5 pb-5">
                <p className="text-foreground-600 text-sm leading-relaxed">{item.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FariaLimaMapCTA() {
  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
              Localização Premium
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-5">
              A poucos passos do metrô{' '}
              <span className="italic font-normal text-foreground-500">Faria Lima</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
              Nosso consultório está estrategicamente localizado na Avenida Brigadeiro Faria Lima, 2355 — 6º andar, a apenas 5 minutos a pé da Estação Faria Lima do metrô. Atendemos profissionais de toda a região: Itaim Bibi, Vila Olímpia, Pinheiros, Jardins, Brooklin e arredores. Estacionamento com valet service disponível no local.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Vim%20pelo%20blog%20exclusivo%20Faria%20Lima%20e%20gostaria%20de%20agendar%20uma%20avaliação.`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer text-center"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Agendar na Faria Lima
              </a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(contactInfo.address)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="whitespace-nowrap border border-foreground-200 text-foreground-700 hover:border-foreground-400 text-sm font-medium px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer text-center"
              >
                <i className="ri-map-pin-line mr-2"></i>
                Como Chegar
              </a>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden h-72 lg:h-80">
            <iframe
              src={contactInfo.googleMapsEmbed}
              title="Consultório Odontológico Dra. Marihana Pasquali - Faria Lima São Paulo"
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}

function FariaLimaBottomCTA() {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
          Seu sorriso merece o melhor da{' '}
          <span className="italic font-normal text-foreground-500">Faria Lima</span>
        </h2>
        <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
          Agende sua avaliação personalizada com a Dra. Marihana Pasquali. Atendimento premium para quem busca excelência odontológica no coração financeiro de São Paulo. Avaliação completa com tecnologia 3D em horários que respeitam sua agenda corporativa.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={`https://wa.me/${contactInfo.whatsapp}?text=Olá!%20Vim%20pelo%20blog%20exclusivo%20Faria%20Lima%20e%20gostaria%20de%20agendar%20uma%20avaliação.`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer"
          >
            <i className="ri-calendar-check-line mr-2"></i>
            Agendar Minha Avaliação
          </a>
          <Link
            to="/"
            className="whitespace-nowrap border border-foreground-200 text-foreground-700 hover:border-foreground-400 text-sm font-medium px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer"
          >
            <i className="ri-home-4-line mr-2"></i>
            Conhecer o Site
          </Link>
        </div>
      </div>
    </section>
  );
}

function FAQSchemaScript() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.slice(0, 5).map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
    />
  );
}

function LocalBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    name: 'Dra. Marihana Pasquali',
    image: 'https://storage.readdy-site.link/project_files/d597c584-8282-4ae1-8b44-377681ee7a13/f075322b-f3ac-4364-a5fe-73fa28de4de4_compressed_DSC07751---Copia---Copia.webp',
    '@id': 'https://dramarihana.com.br',
    url: 'https://dramarihana.com.br',
    telephone: contactInfo.phone,
    email: contactInfo.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Av. Brigadeiro Faria Lima, 2355 - 6º andar',
      addressLocality: 'São Paulo',
      addressRegion: 'SP',
      postalCode: '01452-000',
      addressCountry: 'BR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -23.5832,
      longitude: -46.6847,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '19:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '13:00',
      },
    ],
    description: 'Dra. Marihana Pasquali — Cirurgiã Dentista na Faria Lima, São Paulo. Especialista em cirurgia bucomaxilofacial, estética dental, Invisalign, implantes dentários e reabilitação oral. Atendimento premium no coração financeiro de São Paulo.',
    areaServed: [
      { '@type': 'City', name: 'São Paulo' },
      { '@type': 'Place', name: 'Faria Lima' },
      { '@type': 'Place', name: 'Itaim Bibi' },
      { '@type': 'Place', name: 'Vila Olímpia' },
      { '@type': 'Place', name: 'Pinheiros' },
      { '@type': 'Place', name: 'Jardins' },
      { '@type': 'Place', name: 'Brooklin' },
    ],
    priceRange: '$$$',
    sameAs: [
      `https://www.instagram.com/${contactInfo.instagram}`,
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function BlogListingSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog Odontológico Faria Lima — Dra. Marihana Pasquali',
    description: 'Artigos exclusivos sobre odontologia, saúde bucal e estética dental para pacientes e profissionais da região da Faria Lima, São Paulo. Conteúdos premium sobre clareamento dental, Invisalign, facetas, implantes e odontologia executiva.',
    url: typeof window !== 'undefined' ? window.location.href : '',
    about: {
      '@type': 'Thing',
      name: 'Odontologia na Faria Lima',
    },
    author: {
      '@type': 'Person',
      name: 'Dra. Marihana Pasquali',
      jobTitle: 'Cirurgiã Dentista',
    },
    blogPost: fariaLimaArticles.map((article) => ({
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      url: typeof window !== 'undefined' ? `${window.location.origin}/blog/${article.slug}` : '',
      author: {
        '@type': 'Person',
        name: 'Dra. Marihana Pasquali',
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function FariaLimaBlogPage() {
  const { ref: headerRef, isVisible: headerVisible } = useFadeIn(0.1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <FAQSchemaScript />
      <LocalBusinessSchema />
      <BlogListingSchema />

      <main className="min-h-screen bg-background-50">
        <FariaLimaHero />

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: 'Início', href: '/' },
              { label: 'Blog Faria Lima' },
            ]}
          />
        </div>

        <section className="pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div
              ref={headerRef}
              className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 fade-section ${headerVisible ? 'is-visible' : ''}`}
            >
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-secondary-600 font-semibold mb-3 block">
                  Artigos Premium
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  Conteúdos para quem vive e trabalha na{' '}
                  <span className="italic font-normal text-foreground-500">Faria Lima</span>
                </h2>
                <p className="text-foreground-500 text-sm mt-3 max-w-xl">
                  Odontologia executiva, estética dental, saúde bucal corporativa e muito mais — pensado para o ritmo acelerado do centro financeiro de São Paulo.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-400">
                <i className="ri-article-line"></i>
                <strong className="text-foreground-700">{fariaLimaArticles.length}</strong> artigos exclusivos
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {fariaLimaArticles.map((article, index) => (
                <ArticleCard key={article.id} article={article} index={index} />
              ))}
            </div>
          </div>
        </section>

        <FariaLimaMapCTA />
        <FariaLimaFAQ />
        <FariaLimaBottomCTA />
      </main>
    </>
  );
}